import { UseQueryOptions } from '@tanstack/react-query';
import {
	ChangeConnectionPasswordPath,
	GetUserTenantsPath,
	TenantChangePasswordPayload,
	TenantChangePayload,
	TenantConfig,
	testConnectionPath,
	urlCombine,
	UserConfig,
	UserTenantChangePath,
} from 'ihub/common';
import { CRUDHelperBase } from '../classes/crud-helper-base';
import {
	coreAPI,
	getTenantID,
	handleResponse,
	isURLSet,
	standardGet,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import { cacheStaleTime } from '../constants';
import QueryClientWrapper from '../utils/create-query-client';

export class TenantManagementHttpService extends CRUDHelperBase<TenantConfig> {
	constructor() {
		super(TenantConfig.name);
	}

	getUserTenants(): UseQueryOptions<TenantConfig[], Error, TenantConfig[]> {
		return {
			queryKey: [getTenantID(), this.objectType, GetUserTenantsPath],
			queryFn: async (): Promise<TenantConfig[]> => {
				isURLSet();
				const url = urlCombine(coreAPI, this.objectType, GetUserTenantsPath);
				const res = await standardGet(url);
				await validateOkResponse(res, 'Get User Tenants', TenantConfig.name);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
			retry: true,
			retryDelay: 5000,
		};
	}

	async invalidateUserTenants() {
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, GetUserTenantsPath],
			false
		);
	}

	async refetchAllQueries() {
		await QueryClientWrapper.queryClient.refetchQueries();
	}

	async setUserTenant(
		tenantChangePayload: TenantChangePayload,
		options?: {
			success?: (response?: any) => void | Promise<void>;
			error?: (data: any) => void | Promise<void>;
		}
	) {
		isURLSet();
		const url = urlCombine(coreAPI, this.objectType, UserTenantChangePath);
		const response = await standardPost(url, tenantChangePayload);
		await handleResponse(response, UserTenantChangePath, UserConfig.name, options);
	}

	async changeConnectionPassword(
		tenantChangePasswordPayload: TenantChangePasswordPayload,
		options?: {
			success?: (response?: any) => void | Promise<void>;
			error?: (data: any) => void | Promise<void>;
		}
	) {
		isURLSet();
		const url = urlCombine(coreAPI, this.objectType, ChangeConnectionPasswordPath);
		const response = await standardPost(url, tenantChangePasswordPayload);
		await handleResponse(response, ChangeConnectionPasswordPath, UserConfig.name, options);
	}

	async testConnection(
		tenantConfig: TenantConfig,
		options?: {
			success?: (response?: any) => void | Promise<void>;
			error?: (data: any) => void | Promise<void>;
		}
	) {
		isURLSet();
		const url = urlCombine(coreAPI, this.objectType, testConnectionPath);
		const response = await standardPost(url, tenantConfig);
		await handleResponse(response, testConnectionPath, UserConfig.name, options);
	}
}
